import { useMemo } from 'react';
import * as Yup from 'yup';
import { isNil, isEmpty } from 'ramda';
import { selectTenant } from 'state/tenant';
import { useSelector } from 'react-redux';

import { CreditApplication } from 'state/creditApplication';

import {
  isName,
  isNotPoBox,
  isAddress,
  isCity,
  isZipCode,
  isBeforeToday,
  isBornAfter,
  isSsn,
  isPhoneNumber,
  isAlternatePhoneNumber,
  isNotRestrictedState,
  composeSchema,
  isEmail,
} from 'utils/validations';
import useFieldCustomization from 'hooks/useFieldCustomization';
import useTranslate from 'hooks/useTranslate';
import useFeature from 'hooks/useFeature';

export type FormValues = Pick<
  CreditApplication,
  | 'firstName'
  | 'lastName'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'zip'
  | 'emailAddress'
  | 'dateOfBirth'
  | 'alternativePhone'
  | 'ssn'
  | 'mobilePhone'
  | 'studentApplication'
>;

const useSchema = (): Yup.ObjectSchema<FormValues> => {
  const { isRequired, isLastFour } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const t = useTranslate();
  const tenant = useSelector(selectTenant);
  const { isEnabled } = useFeature();

  const schema = useMemo(() => {
    return Yup.object().shape({
      firstName: Yup.string()
        .label('First Name')
        .nullable()
        .required()
        .max(15)
        .concat(isName),
      lastName: Yup.string()
        .label('Last Name')
        .nullable()
        .required()
        .max(20)
        .concat(isName),
      address1: Yup.string()
        .label('Street')
        .nullable()
        .required()
        .concat(isAddress)
        .concat(isNotPoBox),
      address2: Yup.string()
        .label('Apt./suitetreet')
        .nullable()
        .concat(isAddress)
        .concat(isNotPoBox),
      city: Yup.string()
        .label('City')
        .nullable()
        .concat(isCity)
        .required(),
      state: Yup.string()
        .label('State')
        .nullable()
        .required()
        .concat(isNotRestrictedState(tenant, t('formErrors.restrictedStates'))),
      zip: Yup.string()
        .label('ZIP Code')
        .nullable()
        .required()
        .concat(isZipCode),
      dateOfBirth: Yup.string()
        .label('Date of Birth')
        .nullable()
        .required()
        .concat(isBeforeToday())
        .concat(isBornAfter(1900)),
      alternativePhone: Yup.string()
        .label('Alternate Phone #')
        .nullable()
        .when('mobilePhone', {
          is: value => isNil(value) || isEmpty(value),
          then: Yup.string().required(
            `$\{path} is required if mobile phone is not provided.`,
          ),
        })
        .concat(isAlternatePhoneNumber),
      emailAddress: composeSchema(
        Yup.string()
          .label('Email')
          .nullable()
          .concat(isEmail),
        isRequired('emailAddress') ? Yup.string().required() : null,
      ),
      mobilePhone: Yup.string()
        .label('Mobile #')
        .nullable()
        .concat(isPhoneNumber),
      ssn: composeSchema(
        Yup.string()
          .label(isLastFourBool ? 'Last 4 SSN' : 'SSN')
          .nullable()
          .concat(isSsn(isLastFourBool)),
        isRequired('ssn') ? Yup.string().required() : null,
      ),
      studentApplication: Yup.boolean()
        .label(t('formLabel.studentApplication'))
        .nullable()
        .default(isEnabled('studentApplication') ? false : null),
    });
  }, [tenant, t, isRequired, isLastFourBool, isEnabled]);
  return schema;
};

export default useSchema;
